import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { PublicLayout } from "@/components/shared/PublicLayout";
import { SEOHead } from "@/components/shared/SEOHead";
import { getCareerPosition, submitCareerApplication } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  MapPin, ArrowLeft, Briefcase, Clock, DollarSign, CheckCircle,
  Loader2, Upload, FileText, Share2,
} from "lucide-react";

export default function CareerDetailPage() {
  const { jobSlug } = useParams();
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", cover_letter: "", street: "", city: "", state: "", zip_code: "" });
  const [resume, setResume] = useState(null);

  useEffect(() => {
    getCareerPosition(jobSlug).then(setPosition).catch(console.error).finally(() => setLoading(false));
  }, [jobSlug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!form.street || !form.city || !form.state || !form.zip_code) {
      toast.error("Please enter your full address");
      return;
    }
    if (!resume) {
      toast.error("Please upload your resume");
      return;
    }
    setApplying(true);
    try {
      const fd = new FormData();
      fd.append("position_id", position.id);
      fd.append("first_name", form.first_name);
      fd.append("last_name", form.last_name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("street", form.street);
      fd.append("city", form.city);
      fd.append("state", form.state);
      fd.append("zip_code", form.zip_code);
      fd.append("cover_letter", form.cover_letter || "");
      fd.append("resume", resume);
      await submitCareerApplication(fd);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (err) {
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setApplying(false);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: position.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard");
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-teal" /></div>
      </PublicLayout>
    );
  }

  if (!position) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
          <Briefcase className="w-16 h-16 text-slate-300" />
          <h2 className="font-heading font-bold text-2xl text-navy">Position Not Found</h2>
          <Link to="/careers" className="text-teal hover:underline font-body">Browse all positions</Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <SEOHead
        title={`${position.title} — ${position.location_city}, ${position.location_state} | PMW Careers`}
        description={position.description?.slice(0, 160)}
      />

      {/* Back nav */}
      <div className="bg-slate-50 border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Link to="/careers" className="inline-flex items-center gap-1 text-sm text-teal hover:text-teal/80 font-body" data-testid="back-to-careers">
            <ArrowLeft className="w-4 h-4" /> Back to Open Jobs
          </Link>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-3 gap-10">
          {/* Job Details */}
          <div className="lg:col-span-2 space-y-8">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <h1 className="font-heading font-bold text-3xl text-navy mb-3" data-testid="job-title">{position.title}</h1>
              <div className="flex flex-wrap gap-4 text-sm text-slate-500 font-body">
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4 text-teal" />{position.location_name} — {position.location_city}, {position.location_state}</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-teal" />{position.employment_type}</span>
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={handleShare} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-teal border border-slate-200 rounded-full px-3 py-1" data-testid="share-btn">
                  <Share2 className="w-3 h-3" /> Share
                </button>
              </div>
            </motion.div>

            {/* Description */}
            {position.description && (
              <div data-testid="job-description">
                <h2 className="font-heading font-semibold text-navy text-xl mb-3">About This Role</h2>
                <p className="text-slate-600 font-body leading-relaxed whitespace-pre-line">{position.description}</p>
              </div>
            )}

            {/* Responsibilities */}
            {position.responsibilities?.length > 0 && (
              <div data-testid="job-responsibilities">
                <h2 className="font-heading font-semibold text-navy text-xl mb-3">What You'll Do</h2>
                <ul className="space-y-2">
                  {position.responsibilities.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-600 font-body text-sm">
                      <CheckCircle className="w-4 h-4 text-teal flex-shrink-0 mt-0.5" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Requirements */}
            {position.requirements?.length > 0 && (
              <div data-testid="job-requirements">
                <h2 className="font-heading font-semibold text-navy text-xl mb-3">What We're Looking For</h2>
                <ul className="space-y-2">
                  {position.requirements.map((r, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-600 font-body text-sm">
                      <CheckCircle className="w-4 h-4 text-teal flex-shrink-0 mt-0.5" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Benefits */}
            {position.benefits?.length > 0 && (
              <div data-testid="job-benefits">
                <h2 className="font-heading font-semibold text-navy text-xl mb-3">Why Work With Us</h2>
                <ul className="space-y-2">
                  {position.benefits.map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-slate-600 font-body text-sm">
                      <CheckCircle className="w-4 h-4 text-teal flex-shrink-0 mt-0.5" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Apply Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="apply-form-container">
              {submitted ? (
                <div className="text-center py-6" data-testid="apply-success">
                  <CheckCircle className="w-12 h-12 text-teal mx-auto mb-3" />
                  <h3 className="font-heading font-bold text-navy text-lg mb-2">Application Submitted!</h3>
                  <p className="text-slate-500 font-body text-sm mb-4">Thank you for your interest. We'll review your application and reach out soon.</p>
                  <Link to="/careers" className="text-teal hover:underline font-body text-sm">Browse more positions</Link>
                </div>
              ) : (
                <>
                  <h3 className="font-heading font-bold text-navy text-lg mb-1">Apply Now</h3>
                  <p className="text-slate-500 font-body text-xs mb-5">Fields marked * are required</p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">First Name *</Label>
                        <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="apply-first-name" />
                      </div>
                      <div>
                        <Label className="text-xs">Last Name *</Label>
                        <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="apply-last-name" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Email *</Label>
                      <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="apply-email" />
                    </div>
                    <div>
                      <Label className="text-xs">Phone *</Label>
                      <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} required data-testid="apply-phone" />
                    </div>
                    <div>
                      <Label className="text-xs">Street Address *</Label>
                      <Input value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} placeholder="123 Main St" required data-testid="apply-street" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-xs">City *</Label>
                        <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} required data-testid="apply-city" />
                      </div>
                      <div>
                        <Label className="text-xs">State *</Label>
                        <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="MI" maxLength={2} required data-testid="apply-state" />
                      </div>
                      <div>
                        <Label className="text-xs">Zip *</Label>
                        <Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} placeholder="48001" required data-testid="apply-zip" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Resume (PDF or Word) *</Label>
                      <label className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg p-3 cursor-pointer hover:border-teal/50 transition-colors" data-testid="apply-resume-upload">
                        {resume ? (
                          <><FileText className="w-4 h-4 text-teal" /><span className="text-sm font-body text-navy truncate">{resume.name}</span></>
                        ) : (
                          <><Upload className="w-4 h-4 text-slate-400" /><span className="text-sm font-body text-slate-400">Upload resume...</span></>
                        )}
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                          onChange={(e) => setResume(e.target.files[0] || null)}
                        />
                      </label>
                    </div>
                    <div>
                      <Label className="text-xs">Notes (optional)</Label>
                      <Textarea
                        value={form.cover_letter}
                        onChange={(e) => setForm({ ...form, cover_letter: e.target.value })}
                        placeholder="Any additional notes or information you'd like to share..."
                        rows={4}
                        data-testid="apply-cover-letter"
                      />
                    </div>
                    <Button type="submit" disabled={applying} className="w-full bg-teal hover:bg-teal/90 text-white" data-testid="apply-submit-btn">
                      {applying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Submit Application
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
